import { Suspense } from "react";
import { Skeleton, Stack } from "@material-ui/core";

import type { SettingsSection } from "./settingsSections";

/**
 * Renders one settings section's body, with the suspense boundary it requires.
 *
 * REQUIRED, not defensive: every `Component` in the registry is `React.lazy`, so without a
 * boundary here the first render of any section throws a promise up to whatever boundary
 * happens to exist — on the calendar page that is the route-level one, which would blank
 * the whole calendar behind the open panel.
 *
 * It draws no heading. Both hosts already name the section from the same registry row, in
 * their own chrome: the accordion in its summary row, `SettingsPanel` in its sticky
 * header. (An earlier version rendered an optional heading here for a master-detail
 * content pane; that layout is gone, and with it the prop.)
 */
export function SettingsSectionView({ section }: { section: SettingsSection }): JSX.Element {
  const { Component } = section;

  return (
    <Suspense fallback={<SectionSkeleton />}>
      <Component />
    </Suspense>
  );
}

function SectionSkeleton(): JSX.Element {
  return (
    <Stack gap={1.5}>
      <Skeleton variant="rectangular" height={110} sx={{ borderRadius: 2 }} />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="40%" />
    </Stack>
  );
}
